import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    NewCardForm,
    PartyPropertyEnum
} from "@ferry/journey-services";
import {FailedPaymentMessage} from "./FailedPaymentMessage";
import {Checkbox} from "../inputs/Checkbox";
import {setSessionStorage} from "../../CommonFunctions";
import type {ICustomerPortalService} from "../../services/ICustomerPortalService";
import {Types} from "../../Types";
import {
    getInsurerNameFromPolicyNumber,
    getInsurerNameFromTheme,
    resourcesHelper
} from "@ferry/portal-core";

interface IProps {
    newCardForm?: NewCardForm,
    verifyNewCardFailed: boolean,
    policyNumber?: string
}

interface IState {
    addressConfirmationErrorVisible: boolean,
    addressConfirmed: boolean,
    isBillingAddressChangeAllowed: boolean,
    reuseCard: boolean
}

@authorize
export class CardForm extends React.Component<IProps, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private readonly insurerName = this.props.policyNumber ?
        getInsurerNameFromPolicyNumber(this.props.policyNumber) :
        getInsurerNameFromTheme(resourcesHelper.THEME_NAME);

    constructor(props: IProps) {
        super(props);

        this.state = {
            addressConfirmationErrorVisible: false,
            addressConfirmed: false,
            isBillingAddressChangeAllowed: false,
            reuseCard: false
        };

        this.onAddressConfirmChange = this.onAddressConfirmChange.bind(this);
        this.blockerClick = this.blockerClick.bind(this);
        this.onCardStorageConsentChange = this.onCardStorageConsentChange.bind(this);
    }

    public componentDidMount() {
        setSessionStorage("reuseCard", false);
        this.customerPortalService.getPartyPropertyByPropertyKey(PartyPropertyEnum.BILLING_ADDRESS_CHANGE_ALLOWED)
            .subscribe(partyProperty => {
                this.setState({
                    isBillingAddressChangeAllowed: partyProperty.value === "true"
                });
            });
    }

    public render() {

        if (this.props.newCardForm) {

            const address = this.props.newCardForm.address;
            return (
                <div className="pay-page-padding">
                    {this.props.verifyNewCardFailed &&
                        <FailedPaymentMessage/>}

                    <div className="panel p-0 payment-header-wdg">
                        <div className="row no-gutters payment-header-row secure-payment-header">
                            <h3>Your payment is secure</h3>
                        </div>
                        <div className="row no-gutters payment-header-row secure-payment-body">
                            <p>We use Verified by Visa and MasterCard SecureCode to make sure your payment details are
                                protected.</p>
                            <div className="card-icon-wrapper">
                                <img className="visa-icon" alt="visa-icon" src="/img/visa-icon.svg"/>
                                <img className="mastercard-icon" alt="mastercard-icon" src="/img/mastercard-icon.svg"/>
                            </div>
                        </div>
                        {this.state.isBillingAddressChangeAllowed && <Checkbox
                            className="mt-3 ml-3"
                            label="I consent to my card details being stored securely for any future policy changes I make."
                            name="cardStorageConsentCheckbox"
                            id="cardStorageConsentCheckbox"
                            value={this.state.reuseCard}
                            onChanged={this.onCardStorageConsentChange}
                        />}
                        <div className="row no-gutters payment-header-row">
                            {!this.state.isBillingAddressChangeAllowed && (
                                <div className="col-12">
                                    {address && (
                                        <>
                                            <b>You told us that your {this.insurerName} account holder address is:</b>
                                            <div id="address-display" style={{marginTop: "10px", marginBottom: "10px"}}>
                                                <div
                                                    id="address-line-1">{[address.subBuildingName, address.buildingName].filter(x => x).join(" ")}</div>
                                                <div
                                                    id="address-line-2">{[address.buildingNumber, address.dependentThoroughfareName, address.dependentThoroughfareDescriptor, address.thoroughfareName, address.thoroughfareDescriptor].filter(x => x).join(" ")}</div>
                                                <div
                                                    id="address-line-3">{[address.doubleDependentLocality, address.dependentLocality, address.postTown].filter(x => x).join(" ")}</div>
                                                <div
                                                    id="address-line-4">{[address.outwardPostcode, address.inwardPostcode].filter(x => x).join(" ")}</div>
                                            </div>
                                        </>
                                    )}
                                    <Checkbox
                                        label={"I confirm that my card is registered to the above"}
                                        name={window.name}
                                        id={"payment-address-confirm"}
                                        value={this.state.addressConfirmed}
                                        onChanged={this.onAddressConfirmChange}
                                    />
                                    {this.state.addressConfirmationErrorVisible && (
                                        <div className="invalid-feedback d-block mt-0" role="alert">To proceed you must
                                            confirm</div>
                                    )}
                                    <Checkbox
                                        className="mt-3 ml-3"
                                        label="I consent to my card details being stored securely for any future policy changes I make."
                                        name="cardStorageConsentCheckbox"
                                        id="cardStorageConsentCheckbox"
                                        value={this.state.reuseCard}
                                        onChanged={this.onCardStorageConsentChange}
                                    />
                                    <p>We can only accept payment from a card registered to this address</p>
                                    <p>If you don't have one, please update your details with your card issuer. </p>
                                    <p>If you attempt the payment using a card registered to a different address, your
                                        funds
                                        may result in a pending transaction and your {this.insurerName} insurance
                                        won’t be
                                        set up.</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="pay-page">
                        <div className="payment-gateway-iframe-blockable" tabIndex={-1}>
                            <iframe
                                id="payment-gateway-iframe" title="Payment Page" src={this.props.newCardForm.iframeUrl}
                                className="payment-iframe"
                            />
                            {!this.state.isBillingAddressChangeAllowed && !this.state.addressConfirmed &&
                                <>
                                    <div onClick={this.blockerClick} onKeyDown={this.blockerClick} className="payment-gateway-iframe-button-blocker"
                                         role="alert" tabIndex={-1} aria-hidden={true}/>
                                    <div onClick={this.blockerClick} onKeyDown={this.blockerClick} className="payment-gateway-iframe-blocker"
                                         role="alert" tabIndex={-1} aria-hidden={true}/>
                                </>
                            }
                        </div>
                    </div>
                </div>
            );
        } else {
            return <Overlay show={true}/>
        }
    }

    private onAddressConfirmChange(value: boolean) {
        this.setState({
            addressConfirmed: value,
            addressConfirmationErrorVisible: !value
        })
    }

    private blockerClick() {
        this.setState({
            addressConfirmationErrorVisible: true
        });
        document.getElementById("payment-address-confirm")!.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest"
        });
    }

    private onCardStorageConsentChange(value: boolean) {
        this.setState({
            reuseCard: value
        });
        setSessionStorage("reuseCard", value);
    }
}
